//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useMemo, useRef, useCallback } from 'react';
import { Close } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import LoopDataService from '../../utils/LoopDataService';
import { useUserAuth } from '../../UserAuth';

interface RightPanelProps {
  open: boolean;
  onClose: () => void;
  panelType: 'repository' | 'segment' | 'project' | 'segmentProgress' | null;
  onSubmit: (type: string, data: any) => void;
  existingResources?: Array<{ type?: string; name?: string }>;
  enabledTaskTypes?: Array<{
    displayName: string;
    type: string;
    description: string;
  }>;
  segmentCompleted?: boolean | null;
  teamspaceId?: string | null;
  loopId?: string | null;
  segmentId?: string | null;
  retrySegmentTask?: (tasksHref: string | null) => void;
  segmentStatusByType?: Record<string, string | null>;
  loopSegments?: Array<{ id?: string; displayName?: string; name?: string }>;
  currentLoopId?: string;
  currentLoopName?: string;
  resolveIncompleteSegment?: boolean;
  setResolveIncompleteSegment?: (value: boolean) => void;
  segmentFailed?: boolean;
  segmentCreationError?: string | null;
  setSegmentCreationError?: (value: string | null) => void;
  setSegmentFailed?: (value: boolean) => void;
}

const RightPanel: React.FC<RightPanelProps> = ({
  open,
  onClose,
  panelType,
  onSubmit,
  existingResources = [],
  enabledTaskTypes = [],
  segmentCompleted,
  teamspaceId,
  loopId,
  segmentId,
  retrySegmentTask,
  segmentStatusByType,
  loopSegments = [],
  currentLoopId = '',
  currentLoopName = '',
  resolveIncompleteSegment = false,
  setResolveIncompleteSegment = () => {},
  segmentFailed = false,
  segmentCreationError = '',
  setSegmentCreationError = () => {},
  setSegmentFailed = () => {},
}) => {
  const { t } = useTranslation();
  const [repositoryName, setRepositoryName] = React.useState('');
  const [projectName, setProjectName] = React.useState('');
  const [segmentName, setSegmentName] = React.useState('');
  const [segmentDescription, setSegmentDescription] = React.useState('');
  const [repositoryError, setRepositoryError] = React.useState('');
  const [projectError, setProjectError] = React.useState('');
  const [segmentError, setSegmentError] = React.useState('');
  const [selectedSegmentId, setSelectedSegmentId] = React.useState('');
  const [segmentProgressByType, setSegmentProgressByType] = React.useState<
    Record<string, number>
  >({});
  const [showFinalSuccess, setShowFinalSuccess] = React.useState(false);
  const { user, isAuthenticated } = useUserAuth();

  const MAX_LOOP_NAME_LENGTH = 20;
  const MIN_NAME_LENGTH = 3;

  // Memoize service so its reference stays stable and does not retrigger effects every render
  const loopDataService = useMemo(
    () =>
      new LoopDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  const computeNextProgress = (
    currentProgress: number,
    status: string | null,
  ) => {
    if (!status) return currentProgress;
    if (currentProgress < 90 && status === 'REQUESTED') {
      return currentProgress + 10;
    } else if (status === 'COMPLETED') {
      return 100;
    } else if (currentProgress === 0 && status === 'FAILED') {
      // If task is the active index and for some reason the status is failed but there has never been any progress
      // then we need to increment the progress to show the failed status to the user
      return currentProgress + 10;
    } else {
      return currentProgress;
    }
  };

  React.useEffect(() => {
    if (resolveIncompleteSegment) {
      retrySegmentCreation();
    }
  }, [resolveIncompleteSegment]);

  React.useEffect(() => {
    if (segmentCreationError) {
      setSegmentError(segmentCreationError);
      setSegmentCreationError('');
    }
  }, [segmentCreationError]);

  // Show final success page when segment creation completes
  React.useEffect(() => {
    if (segmentCompleted && panelType === 'segmentProgress') {
      setShowFinalSuccess(true);
    }
  }, [segmentCompleted, panelType]);

  // Reset form when panel opens/closes or type changes
  React.useEffect(() => {
    if (!open) {
      setRepositoryName('');
      setSegmentName('');
      setProjectName('');
      setSegmentName('');
      setSegmentDescription('');
      setRepositoryError('');
      setProjectError('');
      setSegmentError('');
      setSegmentError('');
      setSelectedSegmentId('');
      setShowFinalSuccess(false);
    }
  }, [open, panelType]);

  React.useEffect(() => {
    if (!enabledTaskTypes.length || !segmentStatusByType) return;
    const types = enabledTaskTypes.map((t) => t.type);
    // Decide the "active" index in the SEQUENCE:
    // - tasks with status COMPLETED are considered done
    // - the first non-COMPLETED task in order becomes the active one
    let activeIndex = -1;
    for (let i = 0; i < types.length; i++) {
      const t = types[i];
      const status = segmentStatusByType[t];
      if (status !== 'COMPLETED') {
        activeIndex = i;
        break;
      }
    }

    // If all are COMPLETED, set activeIndex to last so we treat all as done
    if (activeIndex === -1) {
      activeIndex = types.length - 1;
    }

    setSegmentProgressByType((prev) => {
      const next: Record<string, number> = { ...prev };

      enabledTaskTypes.forEach((task, index) => {
        const type = task.type;
        const status = segmentStatusByType[type] ?? null;
        const current = prev[type] ?? 0;

        if (index < activeIndex) {
          // All earlier tasks are visually completed
          next[type] = 100;
        } else if (index === activeIndex) {
          // Only THIS task is allowed to "animate" based on status
          next[type] = computeNextProgress(current, status);
        } else if (status === 'COMPLETED') {
          // Defensive: if backend says completed, respect it
          next[type] = 100;
        } else {
          // Future tasks remain at 0 until they become active
          next[type] = 0;
        }
      });

      return next;
    });
  }, [segmentStatusByType, enabledTaskTypes]);

  // Helper function to get panel title based on panel type
  const getPanelTitle = (
    type: 'repository' | 'segment' | 'project' | 'segmentProgress' | null,
  ): string => {
    const titleMap = {
      repository: t('add-control-repository', 'Add Control Repository'),
      segment: t('add-loop-segment', 'Add Loop Segment'),
      project: t('add-test-project', 'Add Test Project'),
      segmentProgress: t('add-loop-segment', 'Add Loop Segment'),
      null: '',
    };
    return titleMap[type || 'null'];
  };

  // Handler to validate repository name
  const handleRepositoryNameChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value.replace(/\s/g, '');
    setRepositoryName(value);

    // Check for duplicates
    const isDuplicate = existingResources.some(
      (resource) =>
        resource.type === 'CONTROL_REPOSITORY' &&
        resource.name?.toLowerCase() === value.toLowerCase(),
    );

    if (isDuplicate && value.trim() !== '') {
      setRepositoryError(
        t(
          'control-repository-already-exists',
          'Control repository with this name already exists',
        ),
      );
    } else {
      setRepositoryError('');
    }
  };

  // Handler to validate project name
  const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProjectName(value);

    // Check character limit
    if (value.length > 15) {
      setProjectError(
        t(
          'project-name-character-limit',
          'Project name cannot exceed 15 characters',
        ),
      );
      return;
    }

    // Check for duplicates
    const isDuplicate = existingResources.some(
      (resource) =>
        resource.type === 'TEST_PROJECT' &&
        resource.name?.toLowerCase() === value.toLowerCase(),
    );

    if (isDuplicate && value.trim() !== '') {
      setProjectError(
        t(
          'test-project-already-exists',
          'Test project with this name already exists',
        ),
      );
    } else {
      setProjectError('');
    }
  };

  // Ref to hold the debounce timer for segment name duplicate check
  const segmentNameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Debounced backend duplicate check for segment name
  const checkSegmentNameDuplicate = useCallback(
    (value: string) => {
      if (segmentNameDebounceRef.current) {
        clearTimeout(segmentNameDebounceRef.current);
      }

      segmentNameDebounceRef.current = setTimeout(async () => {
        try {
          const duplicateFound = await loopDataService.getSegmentWithName(
            teamspaceId,
            loopId,
            value,
          );

          const isDuplicate = duplicateFound.page.totalElements > 0;
          if (isDuplicate && value.trim() !== '') {
            setSegmentError(
              t(
                'loop-segment-already-exists',
                'Loop segment with this name already exists',
              ),
            );
          }
        } catch (error) {
          setSegmentError(t('error-validating-segment-name'));
        }
      }, 400);
    },
    [loopDataService, teamspaceId, loopId, t],
  );

  // Handler to validate segment name
  const handleSegmentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');
    setSegmentName(value);

    // Allow only letters, numbers, and underscores (no special characters)
    if (!/^[a-zA-Z0-9_]*$/.test(value)) {
      setSegmentError(t('specialCharactersNotAllowed'));
      return;
    }

    const sanitizedInput = value.replace(/\s/g, '_');
    let validInput = sanitizedInput;

    // Check that input length does not exceed maximum
    if (validInput.length > MAX_LOOP_NAME_LENGTH) {
      validInput = validInput.substring(0, MAX_LOOP_NAME_LENGTH);
      setSegmentError(
        t('maxCharacterLimitExceeded', { limit: MAX_LOOP_NAME_LENGTH }),
      );
      return;
    }

    // Check that input length meets minimum
    if (validInput.length > 0 && validInput.length < MIN_NAME_LENGTH) {
      setSegmentError(
        t('loop-segment-name-min-length', { min: MIN_NAME_LENGTH }),
      );
      return;
    }

    // Prevent segment name from matching the parent Loop name
    if (
      currentLoopName &&
      validInput.toLowerCase() === currentLoopName.toLowerCase()
    ) {
      setSegmentError(t('segment-name-same-as-loop'));
      return;
    }

    // Clear error immediately; debounced backend check may set it again
    setSegmentError('');

    // Trigger debounced backend duplicate check
    if (value.trim() !== '') {
      checkSegmentNameDuplicate(value);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Create a FormData object from the form
    const formData = new FormData(e.target as HTMLFormElement);
    const data: Record<string, any> = {};

    // Convert FormData to a regular object
    formData.forEach((value, key) => {
      data[key] = value;
    });

    // Add selectedSegmentId to data if a segment is selected
    if (selectedSegmentId && selectedSegmentId !== 'current-loop') {
      data.segmentId = selectedSegmentId;
    }

    // Call the onSubmit callback with the form data
    onSubmit(panelType || '', data);

    // Close the panel
    if (panelType !== 'segment') {
      onClose();
    }
  };

  const getTaskCompleteText = (segmentType: string) => {
    const matchedTask = enabledTaskTypes.find((task) =>
      task.type.includes(segmentType),
    );
    const descriptionKey = matchedTask?.description;
    if (!descriptionKey) {
      return t('task-complete');
    }
    return t(`${descriptionKey}-complete`);
  };

  const getSegmentProgressToRender = (segmentTypeToRender: string) => {
    if (!segmentStatusByType) {
      return (
        <ProgressBar
          testId={'id-segment-progress-' + segmentTypeToRender}
          value={0}
        />
      );
    }
    const typeKey = Object.keys(segmentStatusByType).find((key) =>
      key.includes(segmentTypeToRender),
    );
    const status = typeKey ? segmentStatusByType?.[typeKey] : 'queued';

    if (status === 'FAILED' && typeKey) {
      return (
        <ProgressBar
          testId={'id-segment-progress-failed-' + segmentTypeToRender}
          value={segmentProgressByType[typeKey]}
          color="bg-destructive"
        />
      );
    } else if (
      (status === 'COMPLETED' || status === 'REQUESTED') &&
      typeKey &&
      segmentProgressByType[typeKey] === 100
    ) {
      return (
        <div
          className="flex items-center gap-2"
          id="id-right-panel-task-complete-row"
          data-testid="right-panel-task-complete-row"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="#029C54">
            <path d="M8 16A8 8 0 1 1 8 0a8 8 0 0 1 0 16zm3.293-10.707L6.586 10 4.707 8.121 3.293 9.536 6.586 12.83l6.121-6.121-1.414-1.416z" />
          </svg>
          <span
            className="text-xs text-secondary-foreground"
            id="id-right-panel-task-complete-text"
            data-testid="right-panel-task-complete-text"
          >
            {getTaskCompleteText(segmentTypeToRender)}
          </span>
        </div>
      );
    } else if (status === 'queued' && typeKey) {
      return (
        <ProgressBar
          testId={'id-segment-progress-' + segmentTypeToRender}
          value={0}
        />
      );
    } else {
      const progressKey = Object.keys(segmentProgressByType).find((key) =>
        key.includes(segmentTypeToRender),
      );
      const progressValue = progressKey
        ? segmentProgressByType[progressKey]
        : 0;
      return (
        <ProgressBar
          testId={'id-segment-progress-' + segmentTypeToRender}
          value={progressValue}
        />
      );
    }
  };

  const getStatusText = (segmentType: string) => {
    // get taskStatus for segmentType
    if (!segmentStatusByType) return t('waiting-in-queue');
    const typeKey = Object.keys(segmentStatusByType).find((key) =>
      key.includes(segmentType),
    );
    const status = typeKey ? segmentStatusByType?.[typeKey] : undefined;
    const progress = typeKey ? segmentProgressByType?.[typeKey] : 0;
    const matchedTask = enabledTaskTypes.find((task) =>
      task.type.includes(segmentType),
    );
    const descriptionKey = matchedTask?.description;
    if (!status || !matchedTask) return t('waiting-in-queue');
    const statusPrefix = t('status') + ': ';
    if (descriptionKey) {
      if (status === 'FAILED' && progress > 0) {
        return statusPrefix + t(`${descriptionKey}-error`);
      } else if (status === 'COMPLETED' && progress === 100) {
        return null;
      } else if (status === 'REQUESTED' && progress > 0) {
        return statusPrefix + t(`${descriptionKey}-requesting`);
      }
    } else {
      if (status === 'FAILED' && progress > 0) {
        return statusPrefix + t('task-error');
      } else if (status === 'COMPLETED' && progress === 100) {
        return null;
      } else if (status === 'REQUESTED' && progress > 0) {
        return statusPrefix + t('building-task');
      }
    }

    return statusPrefix + t('waiting-in-queue');
  };

  const ProgressBar = ({
    value,
    color = 'bg-[#029C54]',
    testId,
  }: {
    value: number;
    color?: string;
    testId?: string;
  }) => (
    <div className="h-2 w-full rounded-full bg-muted" data-testid={testId}>
      <div
        className={`h-2 rounded-full transition-all duration-1000 ${color}`}
        style={{ width: `${value}%` }}
        data-testid={testId ? `${testId}-fill` : undefined}
      />
    </div>
  );

  const closeSegmentPanel = () => {
    // reset ALL progress when closing
    setSegmentProgressByType({});
    onClose();
  };

  const retrySegmentCreation = async () => {
    if (!segmentId || !teamspaceId || !loopId) {
      setResolveIncompleteSegment(false);
      return;
    }

    try {
      const response = await loopDataService.retryLoopSegmentTask(
        teamspaceId,
        loopId,
        segmentId,
      );

      if ('status' in response && response.status === 200) {
        // All tasks already completed successfully — fetch real statuses so
        // the progress UI shows them as done instead of "Waiting in Queue".
        setSegmentFailed(false);
        if (retrySegmentTask) {
          setSegmentProgressByType({});
          // Pass the tasks href if present; the caller will fetch actual
          // statuses and populate segmentStatusByType with COMPLETED values.
          retrySegmentTask(response?._links?.tasks?.href ?? null);
        } else {
          setSegmentError(t('no-tasks-to-retry'));
        }
      } else if (retrySegmentTask) {
        // Segment task has been restarted
        setSegmentProgressByType({});
        setSegmentFailed(false);
        retrySegmentTask(response?._links?.tasks?.href);
      }
    } catch (error) {
      console.error('Failed to retry segment task', error);
    } finally {
      setResolveIncompleteSegment(false);
    }
  };

  const handleSegmentDescriptionChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    setSegmentDescription(value);
  };

  const renderPanelContent = () => {
    switch (panelType) {
      case 'segmentProgress':
        const isDoneDisabled =
          !segmentFailed &&
          (!enabledTaskTypes.length ||
            !enabledTaskTypes.every(({ type }) => {
              const key = Object.keys(segmentProgressByType).find((k) =>
                k.includes(type),
              );
              return key ? segmentProgressByType[key] === 100 : false;
            }));
        return (
          <div
            id="id-segment-progress-panel"
            data-testid="id-segment-progress-panel"
          >
            {showFinalSuccess ? (
              <div
                className="py-12 text-center"
                id="id-right-panel-success-view"
                data-testid="right-panel-success-view"
              >
                <div
                  className="mb-6"
                  id="id-right-panel-success-icon"
                  data-testid="right-panel-success-icon"
                >
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 80 80"
                    fill="none"
                    className="mx-auto"
                  >
                    <circle
                      cx="40"
                      cy="40"
                      r="40"
                      fill="#24a148"
                      opacity="0.1"
                    />
                    <circle cx="40" cy="40" r="30" fill="#24a148" />
                    <path
                      d="M25 40L35 50L55 30"
                      stroke="white"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h2 className="mb-4 text-2xl font-normal text-foreground">
                  {t(
                    'segment-created-successfully',
                    'Segment Created Successfully!',
                  )}
                </h2>
                <p className="mx-auto mb-8 max-w-md text-base text-secondary-foreground">
                  {t(
                    'segment-created-description',
                    `Your ${segmentName} segment has been created and added to Loop Segments. You can now start managing connections for this segment.`,
                  )}
                </p>
                <button
                  onClick={closeSegmentPanel}
                  className="rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
                >
                  {t('done', 'Done')}
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-normal text-foreground">
                  {segmentName} {t('segment-progress')}
                </h3>
                <div
                  className="mt-5 flex w-full flex-col gap-4"
                  id="id-right-panel-task-list"
                  data-testid="right-panel-task-list"
                >
                  {enabledTaskTypes.map((task, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-12 items-center gap-4"
                      id={`id-right-panel-task-row-${index}`}
                      data-testid={`right-panel-task-row-${index}`}
                    >
                      <div
                        className="col-span-3"
                        id={`id-right-panel-task-name-col-${index}`}
                        data-testid={`right-panel-task-name-col-${index}`}
                      >
                        <span
                          className="text-sm font-medium text-foreground"
                          id={`id-right-panel-task-name-${index}`}
                          data-testid={`right-panel-task-name-${index}`}
                        >
                          {task.displayName}
                        </span>
                      </div>
                      <div
                        className="col-span-9"
                        id={`id-right-panel-task-progress-col-${index}`}
                        data-testid={`right-panel-task-progress-col-${index}`}
                      >
                        <div
                          className="mb-1 flex items-center gap-2"
                          id={`id-right-panel-task-status-${index}`}
                          data-testid={`right-panel-task-status-${index}`}
                        >
                          {(() => {
                            const statusText = getStatusText(task.type);
                            const typeKey = segmentStatusByType
                              ? Object.keys(segmentStatusByType).find((key) =>
                                  key.includes(task.type),
                                )
                              : undefined;
                            const status = typeKey
                              ? segmentStatusByType?.[typeKey]
                              : undefined;
                            const progress = typeKey
                              ? segmentProgressByType?.[typeKey]
                              : 0;

                            if (status === 'FAILED' && progress > 0) {
                              return (
                                <>
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="#da1e28"
                                  >
                                    <path d="M8 16A8 8 0 1 1 8 0a8 8 0 0 1 0 16zM4.646 5.354l2.647 2.646-2.647 2.646.708.708L8 8.707l2.646 2.647.708-.708L8.707 8l2.647-2.646-.708-.708L8 7.293 5.354 4.646l-.708.708z" />
                                  </svg>
                                  <span
                                    className="text-xs text-secondary-foreground"
                                    id={`id-right-panel-task-error-text-${index}`}
                                    data-testid={`right-panel-task-error-text-${index}`}
                                  >
                                    {statusText}
                                  </span>
                                </>
                              );
                            } else if (
                              status === 'COMPLETED' &&
                              progress === 100
                            ) {
                              return null;
                            } else {
                              return (
                                <span
                                  className="text-xs text-secondary-foreground"
                                  id={`id-right-panel-task-status-text-${index}`}
                                  data-testid={`right-panel-task-status-text-${index}`}
                                >
                                  {statusText}
                                </span>
                              );
                            }
                          })()}
                        </div>
                        {getSegmentProgressToRender(task.type)}
                      </div>
                    </div>
                  ))}
                </div>
                {!segmentFailed && (
                  <div
                    className="mt-6 flex justify-end gap-4"
                    id="id-right-panel-done-actions"
                    data-testid="right-panel-done-actions"
                  >
                    <button
                      disabled={isDoneDisabled}
                      onClick={closeSegmentPanel}
                      className={`rounded bg-muted px-3 py-1.5 text-xs font-semibold ${
                        isDoneDisabled
                          ? 'cursor-not-allowed text-muted-foreground'
                          : 'hover:bg-muted-hover text-muted-foreground'
                      }`}
                    >
                      {t('done', 'Done')}
                    </button>
                  </div>
                )}
                {segmentError && (
                  <p className="mt-4 text-sm text-destructive">
                    {segmentError}
                  </p>
                )}
                {segmentFailed && !segmentCompleted && (
                  <div
                    className="mt-6 flex justify-end gap-4"
                    id="id-right-panel-retry-actions"
                    data-testid="right-panel-retry-actions"
                  >
                    <button
                      onClick={closeSegmentPanel}
                      className="rounded border border-border px-3 py-1.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary"
                    >
                      {t('cancel')}
                    </button>
                    <button
                      onClick={retrySegmentCreation}
                      className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
                    >
                      {t('retry-now')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        );
      case 'repository':
        return (
          <form onSubmit={handleSubmit}>
            <div
              className="mb-6"
              id="id-right-panel-repo-name-field"
              data-testid="right-panel-repo-name-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('repository-name')} *
              </label>
              <input
                type="text"
                name="repositoryName"
                value={repositoryName}
                onChange={handleRepositoryNameChange}
                placeholder={`${t('enter')} ${t('repository-name').toLowerCase()}`}
                required
                className={`w-full rounded border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none ${
                  repositoryError ? 'border-destructive' : 'border-border'
                }`}
              />
              {repositoryError ? (
                <p className="mt-1 text-xs text-destructive">
                  {repositoryError}
                </p>
              ) : (
                <p className="mt-1 text-xs text-secondary-foreground">
                  {t(
                    'no-spaces-allowed-repo',
                    'Spaces are not allowed in repository name',
                  )}
                </p>
              )}
            </div>
            <div
              className="mb-6"
              id="id-right-panel-repo-description-field"
              data-testid="right-panel-repo-description-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('description-optional')}
              </label>
              <textarea
                name="description"
                value={segmentDescription}
                onChange={(e) => handleSegmentDescriptionChange(e as any)}
                placeholder={`${t('enter')} ${t('description-optional').toLowerCase()}`}
                rows={6}
                className="w-full resize-none rounded border border-border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div
              className="mb-6"
              id="id-right-panel-repo-connection-field"
              data-testid="right-panel-repo-connection-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('add-connection-to', 'Add Connection To')}
              </label>
              <select
                value={selectedSegmentId}
                onChange={(e) => setSelectedSegmentId(e.target.value)}
                className="w-full rounded border border-border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              >
                <option value="current-loop">
                  {currentLoopName || t('current-loop', 'Current Loop')}
                </option>
                {loopSegments &&
                  loopSegments.length > 0 &&
                  loopSegments.map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.displayName || segment.name}
                    </option>
                  ))}
              </select>
            </div>
            <div
              className="flex justify-end"
              id="id-right-panel-repo-submit-actions"
              data-testid="right-panel-repo-submit-actions"
            >
              <button
                type="submit"
                disabled={!!repositoryError || repositoryName.trim() === ''}
                className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:bg-muted"
              >
                {t('add-repository')}
              </button>
            </div>
          </form>
        );
      case 'segment':
        return (
          <form onSubmit={handleSubmit}>
            <div
              className="mb-6"
              id="id-right-panel-segment-name-field"
              data-testid="right-panel-segment-name-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('name')} *
              </label>
              <input
                type="text"
                name="segmentName"
                value={segmentName}
                onChange={handleSegmentNameChange}
                placeholder={`${t('enter')} ${t('segment-name').toLowerCase()}`}
                required
                className={`w-full rounded border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none ${
                  segmentError ? 'border-destructive' : 'border-border'
                }`}
              />
              {segmentError && (
                <p className="mt-1 text-xs text-destructive">{segmentError}</p>
              )}
            </div>
            <div
              className="mb-6"
              id="id-right-panel-segment-description-field"
              data-testid="right-panel-segment-description-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('description-optional')}
              </label>
              <textarea
                name="description"
                placeholder={`${t('enter')} ${t('description-optional').toLowerCase()}`}
                rows={6}
                className="w-full resize-none rounded border border-border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div
              className="flex justify-end"
              id="id-right-panel-segment-submit-actions"
              data-testid="right-panel-segment-submit-actions"
            >
              <button
                type="submit"
                disabled={!!segmentError || segmentName.trim() === ''}
                className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:bg-muted"
              >
                {t('add-segment')}
              </button>
            </div>
          </form>
        );
      case 'project':
        return (
          <form onSubmit={handleSubmit}>
            <div
              className="mb-6"
              id="id-right-panel-project-name-field"
              data-testid="right-panel-project-name-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('project-name')} *
              </label>
              <input
                type="text"
                name="projectName"
                value={projectName}
                onChange={handleProjectNameChange}
                placeholder={`${t('enter')} ${t('project-name').toLowerCase()}`}
                required
                className={`w-full rounded border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none ${
                  projectError ? 'border-destructive' : 'border-border'
                }`}
              />
              {projectError && (
                <p className="mt-1 text-xs text-destructive">{projectError}</p>
              )}
            </div>
            <div
              className="mb-6"
              id="id-right-panel-project-description-field"
              data-testid="right-panel-project-description-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('description-optional')}
              </label>
              <textarea
                name="description"
                placeholder={`${t('enter')} ${t('description-optional').toLowerCase()}`}
                rows={6}
                className="w-full resize-none rounded border border-border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <div
              className="mb-6"
              id="id-right-panel-project-connection-field"
              data-testid="right-panel-project-connection-field"
            >
              <label className="mb-2 block text-xs font-semibold text-secondary-foreground">
                {t('add-connection-to', 'Add Connection To')}
              </label>
              <select
                id="id-segment-select"
                data-testid="id-segment-select"
                value={selectedSegmentId}
                onChange={(e) => setSelectedSegmentId(e.target.value)}
                className="w-full rounded border border-border bg-background px-4 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
              >
                <option value="current-loop">
                  {currentLoopName || t('current-loop', 'Current Loop')}
                </option>
                {loopSegments &&
                  loopSegments.length > 0 &&
                  loopSegments.map((segment) => (
                    <option key={segment.id} value={segment.id}>
                      {segment.displayName || segment.name}
                    </option>
                  ))}
              </select>
            </div>
            <div
              className="flex justify-end"
              id="id-right-panel-project-submit-actions"
              data-testid="right-panel-project-submit-actions"
            >
              <button
                type="submit"
                disabled={!!projectError || projectName.trim() === ''}
                className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:bg-muted"
              >
                {t('add-project')}
              </button>
            </div>
          </form>
        );

      default:
        return null;
    }
  };

  if (!open) return null;

  const handleBackdropClick =
    panelType === 'segmentProgress' ? closeSegmentPanel : onClose;

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        className="fixed inset-0 top-12 z-40 bg-black/20"
        onClick={handleBackdropClick}
        onKeyDown={(e) => {
          if (e.key === 'Escape') handleBackdropClick();
        }}
        id="id-right-panel-backdrop"
        data-testid="right-panel-backdrop"
      />

      {/* Side Panel */}
      <div
        className="fixed bottom-0 right-0 top-12 z-50 flex w-[657px] flex-col bg-card shadow-xl transition-colors"
        id="id-right-panel-container"
        data-testid="right-panel-container"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between border-b border-border bg-muted/20 px-4 py-3"
          id="id-right-panel-header"
          data-testid="right-panel-header"
        >
          <h2 className="text-[16px] font-semibold text-secondary-foreground">
            {getPanelTitle(panelType)}
          </h2>
          <button
            onClick={handleBackdropClick}
            className="rounded p-1 transition-colors hover:bg-muted"
            id="id-close-button"
            data-testid="close-button"
          >
            <Close size={16} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto bg-card p-5"
          id="id-right-panel-content"
          data-testid="right-panel-content"
        >
          {renderPanelContent()}
        </div>
      </div>
    </>
  );
};

export default RightPanel;
